import { FC, useState } from 'react'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import { useIntl } from 'react-intl'
import { useMutation, useQuery } from 'react-query'

import QueueNumbersAPI, { PayloadUpdateQueueNodeStatus, PayloadUpdateQueueNumber } from '@api/queueNumbersAPI'
import { QueuesAPI } from '@api/queuesAPI'

import { ID } from '@models/base'
import { QueueNodeModel } from '@models/queueNode'

import { QueueNumberCompleteFormModal } from '@modules/queues/components/QueueNumberCompleteFormModal'
import { QueueNumberFormModal, QueueNumberFormShape } from '@modules/queues/components/QueueNumberFormModal'

import { POOLING_INTEVAL_MS } from '@/constants/constant'
import { QUERIES } from '@/constants/queries'
import FormUtil from '@/utils/formUtil'
import TypeUtil from '@/utils/typeUtil'

import { QueueNumberCompleteFormShape } from '../QueueNumberCompleteForm'
import { QueueProcessCalled } from '../QueueProcessCalled/QueueProcessCalled'
import { QueueProcessNodeListContainer } from '../QueueProcessNodeListContainer'

type Props = {}

const QueueProcessTopbar: FC<Props> = () => {
  const intl = useIntl()
  const [queueNodeForComplete, setQueueNodeForComplete] = useState<QueueNodeModel>()
  const [queueNumberIdForEdit, setQueueNumberIdForEdit] = useState<ID>()
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false)
  const [isShowCompleteModal, setIsShowCompleteModal] = useState<boolean>(false)

  const { data: queueProcess, refetch: refetchQueueProcess } = useQuery({
    queryKey: [QUERIES.QUEUE_PROCESS],
    queryFn: () => QueuesAPI.process(),
    select: (response) => response.data?.queue_process,
    keepPreviousData: true,
    refetchInterval: POOLING_INTEVAL_MS,
  })

  const { mutateAsync: updateQueueNumber } = useMutation({
    mutationFn: (payload: PayloadUpdateQueueNumber) => QueueNumbersAPI.update(queueNumberIdForEdit!, payload),
    onSuccess: () => {
      setQueueNumberIdForEdit(undefined)
      setIsShowEditModal(false)
      refetchQueueProcess()
    },
  })

  const { mutateAsync: completeQueueNumber } = useMutation({
    mutationFn: (payload: PayloadUpdateQueueNodeStatus) => QueueNumbersAPI.updateStatus('complete', payload),
    onSuccess: () => {
      setQueueNodeForComplete(undefined)
      setIsShowCompleteModal(false)
      refetchQueueProcess()
    },
  })

  const handleUpdateQueueNumber = async (values: QueueNumberFormShape) => {
    const payload = FormUtil.parseValues<PayloadUpdateQueueNumber>(values)
    await updateQueueNumber(payload)
  }

  const handleCompleteQueueNumber = async (values: QueueNumberCompleteFormShape) => {
    const payload = FormUtil.parseValues<PayloadUpdateQueueNodeStatus>(values)
    await completeQueueNumber({
      ...payload,
      queue_number_id: queueNodeForComplete ? queueNodeForComplete?.queue_number_id : undefined,
    })
  }

  const handleClickEdit = (node: QueueNodeModel) => {
    setQueueNumberIdForEdit(node.queue_number_id)
    setIsShowEditModal(true)
  }

  const handleClickCompleteFromCalled = () => {
    setQueueNodeForComplete(undefined)
    setIsShowCompleteModal(true)
  }

  const handleClickCompleteFromStandby = (node: QueueNodeModel) => {
    setQueueNodeForComplete(node)
    setIsShowCompleteModal(true)
  }

  const tabTitle = (title: string, totalQueueNode: number) => {
    return (
      <div className="d-flex">
        {title}

        {!TypeUtil.isEmpty(totalQueueNode) && (
          <span className="badge badge-sm badge-circle badge-danger ms-3">{totalQueueNode}</span>
        )}
      </div>
    )
  }

  if (!queueProcess) {
    return null
  }

  return (
    <>
      <QueueProcessCalled
        currentNode={queueProcess.queue_counter?.called_nodes?.at(0)}
        hasNextQueue={!!queueProcess.waiting_nodes?.length}
        onComplete={handleClickCompleteFromCalled}
        onEdit={handleClickEdit}
      />

      <div className="mt-5">
        <Tabs defaultActiveKey="waiting">
          <Tab
            className="pt-3"
            eventKey="waiting"
            title={intl.formatMessage({ id: 'queue.tab.pending' })}
          >
            <div className="mh-200px overflow-y-auto">
              <QueueProcessNodeListContainer
                isDraggable
                queueId={queueProcess.id}
                queueNodes={queueProcess.waiting_nodes ?? []}
                onEdit={handleClickEdit}
              />
            </div>
          </Tab>
          <Tab
            className="pt-3"
            eventKey="standby"
            title={tabTitle(
              intl.formatMessage({ id: 'queue.tab.standby' }),
              queueProcess.queue_counter?.stand_by_nodes?.length ?? 0
            )}
          >
            <div className="mh-200px overflow-y-auto">
              <QueueProcessNodeListContainer
                isCallable
                isCompletable
                queueId={queueProcess.id}
                queueNodes={queueProcess.queue_counter?.stand_by_nodes ?? []}
                onEdit={handleClickEdit}
                onComplete={handleClickCompleteFromStandby}
              />
            </div>
          </Tab>
          <Tab
            className="pt-3"
            eventKey="skipped"
            title={tabTitle(intl.formatMessage({ id: 'queue.tab.skipped' }), queueProcess.skipped_nodes?.length ?? 0)}
          >
            <div className="mh-200px overflow-y-auto">
              <QueueProcessNodeListContainer
                isRedeployable
                queueId={queueProcess.id}
                queueNodes={queueProcess.skipped_nodes ?? []}
                onEdit={handleClickEdit}
              />
            </div>
          </Tab>
        </Tabs>
      </div>

      <QueueNumberCompleteFormModal
        isShow={isShowCompleteModal}
        queueId={queueProcess.id}
        isEnding={queueProcess.is_ending}
        preferredNextQueue={queueProcess.preferred_next_queue}
        onClose={() => setIsShowCompleteModal(false)}
        onSubmit={handleCompleteQueueNumber}
      />
      
      {
        queueNumberIdForEdit && (
          <QueueNumberFormModal
            isGenerate={false}
            isShow={isShowEditModal}
            onExited={() => setQueueNumberIdForEdit(undefined)}
            queueNumberId={queueNumberIdForEdit}
            disabledFields={{
              cart_type: true,
              queue_number_type: true,
              queue_id: true,
            }}
            currentQueue={{
              id: queueProcess.id,
              name: queueProcess.name,
              allow_multiple_process: queueProcess.allow_multiple_process,
            }}
            onClose={() => setIsShowEditModal(false)}
            onSubmit={handleUpdateQueueNumber}
          />
        )
      }
    </>
  )
}

export { QueueProcessTopbar }
